import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../config/supabase';
import FilterPanel from '../components/FilterPanel';
import EventCard from '../components/EventCard';
import NoEventsMessage from '../components/NoEventsMessage';
import NoLocationMessage from '../components/NoLocationMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import { useFilteredEvents } from '../hooks/useFilteredEvents';
import { 
  // createImportJob, 
  hasRecentJob as checkHasRecentJob 
} from '../utils/importJobsUtils';
import { getBulkEventStats7Days } from '../utils/statsUtils';

/**
 * Page de liste des événements
 * Affiche la liste des événements filtrables
 */
const EventListPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const todayRef = useRef(null);
  const redLineRef = useRef(null);
  const eventsFetchedRef = useRef(false);
  const categoriesFetchedRef = useRef(false);
  const ageGroupsFetchedRef = useRef(false);
  
  const [showFilters, setShowFilters] = useState(false);
  
  // Restaurer les filtres depuis localStorage ou utiliser les valeurs par défaut
  const [filterDate, setFilterDate] = useState(() => {
    const saved = localStorage.getItem('filterDate');
    if (saved) return saved;
    const d = new Date();
    return d.toISOString().slice(0, 10);
  });
  
  const [filterEndDate, setFilterEndDate] = useState(() => {
    const saved = localStorage.getItem('filterEndDate');
    if (saved) return saved;
    const d = new Date();
    d.setDate(d.getDate() + 7); // +7 jours par défaut
    return d.toISOString().slice(0, 10);
  });
  
  const [filterLocation, setFilterLocation] = useState(() => 
    localStorage.getItem('filterLocation') || ''
  );
  
  const [communeName, setCommuneName] = useState(() => 
    localStorage.getItem('communeName') || ''
  );
  
  const [communeInseeCode, setCommuneInseeCode] = useState(() => 
    localStorage.getItem('communeInseeCode') || ''
  );
  
  const [searchPosition, setSearchPosition] = useState(() => {
    const saved = localStorage.getItem('searchPosition');
    return saved ? JSON.parse(saved) : null;
  });
  
  const [filterDistance, setFilterDistance] = useState(() => {
    const saved = localStorage.getItem('filterDistance');
    return saved ? Number(saved) : 3;
  });
  
  const [nearbyPostalCodes, setNearbyPostalCodes] = useState([]);
  const [nearbyCommuneInsee, setNearbyCommuneInsee] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const [filterCategories, setFilterCategories] = useState(() => {
    const saved = localStorage.getItem('filterCategories');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [ageGroups, setAgeGroups] = useState([]);
  
  const [filterAgeGroup, setFilterAgeGroup] = useState(() => 
    localStorage.getItem('filterAgeGroup') || ''
  );
  
  const [filterAllWeather, setFilterAllWeather] = useState(() => 
    localStorage.getItem('filterAllWeather') === 'true'
  );
  
  const [filterAccessible, setFilterAccessible] = useState(() => 
    localStorage.getItem('filterAccessible') === 'true'
  );
  
  const [filterFree, setFilterFree] = useState(() => 
    localStorage.getItem('filterFree') === 'true'
  );
  
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [generatingEvents, setGeneratingEvents] = useState(false);
  const [generateMessage, setGenerateMessage] = useState('');
  const [hasRecentJob, setHasRecentJob] = useState(false);
  const [checkingJob, setCheckingJob] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [isFilteringComplete, setIsFilteringComplete] = useState(false);
  const [isInitialRenderReady, setIsInitialRenderReady] = useState(false);
  const [eventStats, setEventStats] = useState({});

  // Cache pour éviter les appels multiples
  const eventsCache = useRef(null);
  const categoriesCache = useRef(null);
  const ageGroupsCache = useRef(null);
  const cacheTimestamp = useRef(null);
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  // Sauvegarder les filtres dans localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem('filterDate', filterDate);
  }, [filterDate]);

  useEffect(() => {
    localStorage.setItem('filterEndDate', filterEndDate);
  }, [filterEndDate]);

  useEffect(() => {
    localStorage.setItem('filterLocation', filterLocation);
  }, [filterLocation]);

  useEffect(() => {
    localStorage.setItem('communeName', communeName);
  }, [communeName]);

  useEffect(() => {
    localStorage.setItem('communeInseeCode', communeInseeCode);
  }, [communeInseeCode]);

  useEffect(() => {
    if (searchPosition) {
      localStorage.setItem('searchPosition', JSON.stringify(searchPosition));
    }
  }, [searchPosition]);

  // useEffect(() => {
  //   if (userPosition) {
  //     localStorage.setItem('userPosition', JSON.stringify(userPosition));
  //   }
  // }, [userPosition]);

  useEffect(() => {
    localStorage.setItem('filterDistance', filterDistance.toString());
  }, [filterDistance]);

  useEffect(() => {
    localStorage.setItem('filterCategories', JSON.stringify(filterCategories));
  }, [filterCategories]);

  useEffect(() => {
    localStorage.setItem('filterAgeGroup', filterAgeGroup);
  }, [filterAgeGroup]);

  useEffect(() => {
    localStorage.setItem('filterAllWeather', filterAllWeather.toString());
  }, [filterAllWeather]);

  useEffect(() => {
    localStorage.setItem('filterAccessible', filterAccessible.toString());
  }, [filterAccessible]);

  useEffect(() => {
    localStorage.setItem('filterFree', filterFree.toString());
  }, [filterFree]);

  // Supprimer l'ancien useEffect de récupération des événements et utiliser le hook
  const { events, loading, error } = useFilteredEvents({
    searchPosition,
    nearbyPostalCodes,
    filterDate,
    filterEndDate,
    filterCategories,
    filterAgeGroup,
    filterAllWeather,
    filterAccessible,
    filterFree,
    enabled: true
  });

  // Récupérer la liste des catégories actives avec cache
  useEffect(() => {
    // Protection contre les doubles exécutions
    if (categoriesFetchedRef.current) {
      console.log('[Categories] Déjà récupérées - ignoré');
      return;
    }

    const fetchCategories = async () => {
      // Vérifier le cache
      if (categoriesCache.current) {
        console.log('[Categories] Utilisation du cache');
        setCategories(categoriesCache.current);
        return;
      }

      console.log('[Categories] Récupération des catégories');
      categoriesFetchedRef.current = true;
      
      const { data, error } = await supabase
        .from('categories')
        .select('id, name, icon, color_code')
        .eq('is_active', true)
        .order('name', { ascending: true });
      
      if (error) {
        console.error('[Categories] Erreur Supabase:', error);
      } else {
        console.log(`[Categories] ${data?.length || 0} catégories récupérées`);
        categoriesCache.current = data || [];
        setCategories(data || []);
      }
    };
    
    fetchCategories();
  }, []); // Dépendances vides - exécuter une seule fois

  // Récupérer la liste des classes d'âge avec cache
  useEffect(() => {
    // Protection contre les doubles exécutions
    if (ageGroupsFetchedRef.current) {
      console.log('[AgeGroups] Déjà récupérées - ignoré');
      return;
    }

    const fetchAgeGroups = async () => {
      // Vérifier le cache
      if (ageGroupsCache.current) {
        console.log('[AgeGroups] Utilisation du cache');
        setAgeGroups(ageGroupsCache.current);
        return;
      }

      console.log('[AgeGroups] Récupération des classes d\'âge');
      ageGroupsFetchedRef.current = true;
      
      const { data, error } = await supabase
        .from('age_groups')
        .select('id, name, icon, min_age, max_age')
        .order('min_age', { ascending: true });
      
      if (error) {
        console.error('[AgeGroups] Erreur Supabase:', error);
      } else {
        console.log(`[AgeGroups] ${data?.length || 0} classes d'âge récupérées`);
        ageGroupsCache.current = data || [];
        setAgeGroups(data || []);
      }
    };
    
    fetchAgeGroups();
  }, []); // Dépendances vides - exécuter une seule fois

  // Vérifier s'il existe un job récent non terminé pour ce code postal
  useEffect(() => {
    const checkRecentJob = async () => {
      if (!filterLocation) {
        console.log('[Job Check] Pas de filterLocation défini');
        setHasRecentJob(false);
        return;
      }

      console.log(`[Job Check] 🔍 Recherche de jobs pour le code postal: "${filterLocation}"`);
      setCheckingJob(true);
      
      try {
        const result = await checkHasRecentJob(filterLocation, 1);
        console.log(`[Job Check] ✔️ Vérification terminée. hasRecentJob = ${result}`);
        setHasRecentJob(result);
      } catch (error) {
        console.error('[Job Check] ❌ Exception lors de la vérification des jobs:', error);
        setHasRecentJob(false);
      } finally {
        setCheckingJob(false);
      }
    };

    checkRecentJob();
  }, [filterLocation]);

  // Déterminer si le rendu initial est prêt
  useEffect(() => {
    if (loading) {
      setIsInitialRenderReady(false);
      return;
    }

    if (searchPosition) {
      const isReady = nearbyPostalCodes.length > 0;
      setIsInitialRenderReady(isReady);
      
      if (isReady) {
        console.log('[EventList] ✅ Rendu prêt:', { 
          nearbyPostalCodesLength: nearbyPostalCodes.length,
          eventsLength: events.length
        });
      }
    } else {
      setIsInitialRenderReady(true);
    }
  }, [loading, searchPosition, nearbyPostalCodes.length, events.length]);

  // Les événements sont déjà filtrés, on ajoute juste isCreator
  const filteredEvents = useMemo(() => {
    return events.map(event => {
      const isCreator = currentUser && event.organizer_id === currentUser.id;
      return {
        ...event,
        isCreator
      };
    });
  }, [events, currentUser]);

  // Marquer le filtrage comme terminé
  useEffect(() => {
    if (isInitialRenderReady && !loading) {
      setIsFilteringComplete(true);
    }
  }, [isInitialRenderReady, loading]);

  // Grouper les événements par date avec useMemo pour éviter les recalculs
  const groupedEvents = useMemo(() => {
    const groups = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    filteredEvents.forEach(event => {
      if (!event.start_date) return;
      const startDate = new Date(event.start_date);
      const endDate = event.end_date ? new Date(event.end_date) : null;

      let key;
      let isToday = false;
      let sortDate = startDate; // Date utilisée pour le tri
      let isOngoing = false; // Indicateur pour événement en cours
      
      // Calculer isPast basé sur end_date si disponible, sinon start_date
      const eventDate = endDate || startDate;
      const isPast = eventDate < today;
      
      // Si l'événement a déjà commencé (date de début dans le passé)
      if (startDate < today && endDate && endDate >= today) {
        // Afficher "Jusqu'au" + date de fin
        const formattedEndDate = endDate.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' });
        key = `${t('eventList.until')} ${formattedEndDate}`;
        sortDate = endDate; // Utiliser la date de fin pour le tri
        isOngoing = true;
      } else if (startDate.toDateString() === today.toDateString()) {
        key = t('common.today');
        isToday = true;
      } else {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        if (startDate.toDateString() === tomorrow.toDateString()) {
          key = t('common.tomorrow');
        } else {
          key = startDate.toLocaleDateString(i18n.language, { weekday: 'long', day: 'numeric', month: 'long' });
        }
      }
      
      if (!groups[key]) {
        groups[key] = { events: [], isToday, date: sortDate, isOngoing };
      }
      
      // Ajouter isPast à l'événement
      groups[key].events.push({
        ...event,
        isPast
      });
    });
    return groups;
  }, [filteredEvents, t, i18n.language]);

  // Événements mis en avant avec useMemo
  const featuredEvents = useMemo(() => {
    return filteredEvents.filter(event => event.is_featured);
  }, [filteredEvents]);

  // Vérifier s'il y a des événements aujourd'hui avec useMemo
  const hasEventsToday = useMemo(() => {
    return Object.values(groupedEvents).some(group => group.isToday);
  }, [groupedEvents]);

  // Trouver la première date future avec useMemo
  const sortedGroups = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Object.entries(groupedEvents).sort((a, b) => {
      return a[1].date - b[1].date;
    });
  }, [groupedEvents]);

  const firstFutureIndex = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return sortedGroups.findIndex(([_, group]) => group.date >= today);
  }, [sortedGroups]);

  const handleApplyFilters = () => {
    setShowFilters(false);
    console.log('[Filters] Application des filtres');
    console.log('[Filters] Codes postaux à proximité:', nearbyPostalCodes.map(pc => `${pc.code_postal} - ${pc.nom_de_la_commune}`));
    console.log('[Filters] Distance:', filterDistance, 'km');
    console.log('[Filters] Position:', searchPosition);
    console.log('[Filters] Nombre total d\'événements:', events.length);
  };

  // Callback pour gérer la sélection de commune depuis NoLocationMessage
  const handleSelectCommuneFromNoLocation = async (commune) => {
    console.log('[NoLocationMessage] Commune sélectionnée:', commune);
    
    // Mettre à jour les états de localisation
    setFilterLocation(commune.codePostal);
    setCommuneName(commune.nom);
    setCommuneInseeCode(commune.code);
    
    // Sauvegarder dans localStorage
    localStorage.setItem('filterLocation', commune.codePostal);
    localStorage.setItem('communeName', commune.nom);
    localStorage.setItem('communeInseeCode', commune.code);
    
    // Si nous avons un centre de commune depuis l'API Géo
    if (commune.centre) {
      const position = {
        latitude: commune.centre.coordinates[1],
        longitude: commune.centre.coordinates[0]
      };
      setSearchPosition(position);
      localStorage.setItem('searchPosition', JSON.stringify(position));
      localStorage.setItem('lastSearchTime', Date.now().toString());
      
      console.log('[NoLocationMessage] Position mise à jour:', position);
    } else {
      // Sinon, récupérer le centroïde depuis Supabase
      try {
        const { data, error } = await supabase
          .from('communes')
          .select('centroide')
          .eq('code', commune.code)
          .single();
        
        if (!error && data && data.centroide) {
          const position = {
            latitude: data.centroide.coordinates[1],
            longitude: data.centroide.coordinates[0]
          };
          setSearchPosition(position);
          localStorage.setItem('searchPosition', JSON.stringify(position));
          localStorage.setItem('lastSearchTime', Date.now().toString());
          
          console.log('[NoLocationMessage] Position récupérée depuis Supabase:', position);
        }
      } catch (err) {
        console.error('[NoLocationMessage] Erreur lors de la récupération du centroïde:', err);
      }
    }
  };

  // // Fonction pour réinitialiser les dates - SUPPRIMER si non utilisée par FilterPanel
  // // Cette fonction devrait être gérée dans FilterPanel directement

  // Récupérer l'utilisateur connecté au chargement du composant
  useEffect(() => {
    const getUser = async () => {
      try {
        const { data: { user }, error } = await supabase.auth.getUser();
        if (error) {
          // Ne logger que si ce n'est pas une erreur de session manquante
          if (error.message !== 'Auth session missing!' && !error.message.includes('session')) {
            console.error("Erreur lors de la récupération de l'utilisateur:", error);
          }
        } else {
          setCurrentUser(user);
          
          // Récupérer le profil pour vérifier le rôle
          if (user) {
            const { data: roleData, error: roleError } = await supabase
              .from('roles')
              .select('role')
              .eq('id', user.id)
              .maybeSingle();
            
            if (!roleError && roleData) {
              setIsAdmin(roleData.role === 'admin');
            }
          }
        }
      } catch (err) {
        // Ne logger que si ce n'est pas une erreur de session manquante
        if (err.message !== 'Auth session missing!' && !err.message.includes('session')) {
          console.error("Exception lors de la vérification de l'authentification:", err);
        }
      }
    };
    
    getUser();
  }, []);

  // Récupérer les favoris de l'utilisateur
  useEffect(() => {
    const fetchFavorites = async () => {
      if (!currentUser) {
        setFavorites([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('favorites')
          .select('event_id')
          .eq('user_id', currentUser.id);

        if (error) {
          console.error("Erreur lors de la récupération des favoris:", error);
        } else {
          const favIds = data?.map(fav => fav.event_id) || [];
          console.log('[Favorites] Favoris récupérés:', favIds.length, 'événement(s)');
          setFavorites(favIds);
        }
      } catch (err) {
        console.error("Exception lors de la récupération des favoris:", err);
      }
    };

    fetchFavorites();
  }, [currentUser]);

  // Vérifier si un événement est en favori
  const isFavorite = (eventId) => {
    return favorites.includes(eventId);
  };

  // Fonction pour créer un job d'importation d'événements
  // const handleGenerateEvents = async () => {
  //   if (!currentUser) {
  //     setGenerateMessage(t('common.notAuthenticated'));
  //     setTimeout(() => setGenerateMessage(''), 3000);
  //     return;
  //   }

  //   if (!filterLocation) {
  //     setGenerateMessage(t('eventList.selectLocation'));
  //     setTimeout(() => setGenerateMessage(''), 3000);
  //     return;
  //   }

  //   setGeneratingEvents(true);
  //   setGenerateMessage('');
    
  //   try {
  //     await createImportJob(currentUser.id, filterLocation);
  //     setGenerateMessage(t('settings.admin.importCommune.status.jobCreated'));
  //   } catch (error) {
  //     console.error("Erreur lors du lancement du job:", error);
  //     setGenerateMessage(error.message || t('settings.admin.importCommune.status.error'));
  //   } finally {
  //     setGeneratingEvents(false);
  //     setTimeout(() => setGenerateMessage(''), 5000);
  //   }
  // };

  // Récupérer les statistiques pour les événements de l'utilisateur
  useEffect(() => {
    const fetchStats = async () => {
      if (!currentUser || filteredEvents.length === 0) {
        setEventStats({});
        return;
      }

      // Trouver les IDs des événements créés par l'utilisateur
      const userEventIds = filteredEvents
        .filter(event => event.organizer_id === currentUser.id)
        .map(event => event.id);

      if (userEventIds.length === 0) {
        setEventStats({});
        return;
      }

      console.log('[Stats] Récupération des statistiques pour', userEventIds.length, 'événements');
      const stats = await getBulkEventStats7Days(userEventIds);
      setEventStats(stats);
      console.log('[Stats] Statistiques récupérées:', stats);
    };

    fetchStats();
  }, [currentUser, filteredEvents]);

  return (
    
    <div className="h-screen flex flex-col">
      {/* Panneau de filtres */}
      <FilterPanel
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filterDate={filterDate}
        setFilterDate={setFilterDate}
        filterEndDate={filterEndDate}
        setFilterEndDate={setFilterEndDate}
        filterLocation={filterLocation}
        setFilterLocation={setFilterLocation}
        communeName={communeName}
        setCommuneName={setCommuneName}
        communeInseeCode={communeInseeCode}
        setCommuneInseeCode={setCommuneInseeCode}
        searchPosition={searchPosition}
        setSearchPosition={setSearchPosition}
        filterDistance={filterDistance}
        setFilterDistance={setFilterDistance}
        nearbyPostalCodes={nearbyPostalCodes}
        setNearbyPostalCodes={setNearbyPostalCodes}
        setNearbyCommuneInsee={setNearbyCommuneInsee}
        filterCategories={filterCategories}
        setFilterCategories={setFilterCategories}
        categories={categories}
        filterAgeGroup={filterAgeGroup}
        setFilterAgeGroup={setFilterAgeGroup}
        ageGroups={ageGroups}
        filterAllWeather={filterAllWeather}
        setFilterAllWeather={setFilterAllWeather}
        filterAccessible={filterAccessible}
        setFilterAccessible={setFilterAccessible}
        filterFree={filterFree}
        setFilterFree={setFilterFree}
        onApply={handleApplyFilters}
        eventsCount={events.length}
      />

      {/* Liste des événements par date */}
      <div className="h-16 shrink-0 "/>
      <div className="px-4 mb-50 mt-5">
        {(loading || !isInitialRenderReady) && (
          <LoadingSpinner />
        )}
        {/* Aucune localisation */}
          {!loading && isInitialRenderReady && !searchPosition && (
            <div className="flex items-center justify-center min-h-[calc(70vh-8rem)]">
              <NoLocationMessage 
                onSelectCommune={handleSelectCommuneFromNoLocation}
              />
            </div>
          )}
          {/* Aucun évènement */}
        {!loading && isInitialRenderReady && searchPosition && isFilteringComplete && filteredEvents.length === 0 && (
            <div className="flex items-center justify-center min-h-[calc(70vh-8rem)]">
          <NoEventsMessage 
            cityName={communeName}
            postalCode={filterLocation}
          />
            </div>
        )}
        {isInitialRenderReady && sortedGroups.map(([date, { events: eventsForDate, isToday }], index) => (
          <React.Fragment key={date}>
            {/* Ligne rouge si pas d'événement aujourd'hui et c'est la transition passé/futur */}
            {!hasEventsToday && index === firstFutureIndex && (
              <div 
                ref={redLineRef}
                className="relative py-4"
              >
                <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-red-500 shadow-sm"></div>
              </div>
            )}
            
            <section className="mb-6">
              <div 
                ref={isToday ? todayRef : null}
                className={`relative ${isToday ? 'pt-1' : ''}`}
              >
                {/* Trait rouge horizontal pour la date du jour avec événements */}
                {isToday && (
                  <div className="absolute -top-1 left-0 right-0 h-0.5 bg-red-500 shadow-sm"></div>
                )}
                <h2 className={`text-lg font-medium mb-2 mt-2 ${isToday ? 'text-red-600 font-bold' : ''}`}>
                  {date}
                </h2>
              </div>
              <div className="space-y-4">
                {eventsForDate.map(event => (
                  <EventCard
                    key={event.id}
                    event={event}
                    onClick={() => navigate(`/event/${event.id}`)}
                    isPast={event.isPast}
                    isFavorite={isFavorite(event.id)}
                    isCreator={event.isCreator}
                    stats={event.isCreator ? (eventStats[event.id] || []) : []}
                    categoryColorCode={event.category?.color_code}
                  />
                ))}
              </div>
            </section>
          </React.Fragment>
        ))}
      </div>
      <div className="h-16 shrink-0 "/>
    </div>
  );
};

export default EventListPage;
